import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PageWrapper } from '../components/PageWrapper';
import PageHero from '../components/PageHero';

const mockReviews = {
  reviews: [
    { _id:'1', guestName:'Priya Menon', location:'Bangalore, India', rating:5, title:'Like a Tavaru Mane', content:'Villa Tesori felt exactly like a second home. Nimmy made us feel like family from the first moment. The food, the views, the warmth — everything was beyond expectations. We celebrated our daughter\'s birthday here and Nimmy even baked a surprise cake!', stayDate:'2024-11-01' },
    { _id:'2', guestName:'Sarah & Tom', location:'London, UK', rating:5, title:'Magical Wayanad Experience', content:'We\'ve traveled across Asia, but Villa Tesori stands out. The 360° views of Chembra Peak, the sound of the stream, the home-cooked Kerala food — pure magic. Nimmy and Raphi are exceptional hosts who make you feel the real soul of Kerala.', stayDate:'2024-10-15' },
    { _id:'3', guestName:'Arjun Nair', location:'Mumbai, India', rating:5, title:'Best Homestay in Kerala', content:'It\'s a blend of comfort and adventure. Super cute, clean, beautiful rooms with the perfect views. The host took us for an adventurous waterfall trek and captured amazing photos. Would definitely re-visit!', stayDate:'2024-09-20' },
    { _id:'4', guestName:'Fatima Al-Hassan', location:'Dubai, UAE', rating:5, title:'Hidden Treasure Discovered', content:'Tesori means treasure and that\'s exactly what this place is. A hidden gem in the Western Ghats. The peaceful atmosphere, the herbal tea from nearby gardens, the misty mornings — I\'m already planning my return.', stayDate:'2024-08-05' },
    { _id:'5', guestName:'Ravi & Deepa', location:'Chennai, India', rating:5, title:'Anniversary Made Perfect', content:'Our 10th anniversary here was unforgettable. Nimmy went above and beyond with decorations and a special Kerala dinner. The Chembra Peak sunrise from our room balcony was worth the entire trip.', stayDate:'2024-07-14' },
    { _id:'6', guestName:'James Okonkwo', location:'Lagos, Nigeria', rating:5, title:'World-Class Hospitality', content:'Having lived in multiple countries, I can say Villa Tesori offers hospitality that rivals the world\'s best. Raphi\'s multicultural perspective and Nimmy\'s warmth create something truly special here in the hills of Kerala.', stayDate:'2024-06-30' },
  ],
  avgRating: 5.0,
  total: 14
};

export default function Reviews() {
  const [form, setForm] = useState({ guestName:'', guestEmail:'', location:'', rating:5, title:'', content:'' });
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['reviews'],
    queryFn: () => axios.get('/api/reviews').then(r => r.data).catch(() => mockReviews)
  });

  const reviewData = data || mockReviews;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post('/api/reviews', form);
      toast.success('Thank you! Your review will appear after approval.');
      setForm({ guestName:'', guestEmail:'', location:'', rating:5, title:'', content:'' });
      queryClient.invalidateQueries(['reviews']);
    } catch {
      toast.error('Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>Guest Reviews – Villa Tesori | Wayanad Kerala</title>
        <meta name="description" content="Read what guests say about Villa Tesori, the Diamond Class certified homestay in Vellarimala, Wayanad. 5-star rated on TripAdvisor." />
      </Helmet>

      <PageHero
        scriptText="Guest Stories"
        title="Reviews & Testimonials"
        subtitle="Real experiences, genuine hearts — stories from our treasured guests"
        bgImage="/reviewherobg.jpeg"
        bgPosition="top center"
        overlayOpacity={0.45}
      />

      {/* Stats */}
      <section className="section-sm" style={{ background: 'var(--forest)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 32, textAlign: 'center' }}>
            {[
              { num: reviewData.avgRating, label: 'Average Rating', icon: '⭐' },
              { num: reviewData.total, label: 'Total Reviews', icon: '💬' },
              { num: '100%', label: '5-Star Reviews', icon: '🏆' },
              { num: '#2', label: 'Vellarimala on TripAdvisor', icon: '🌐' },
            ].map(s => (
              <div key={s.label}>
                <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.6rem', color: 'var(--gold-light)' }}>{s.num}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(200,217,192,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="section">
        <div className="container">
          <div className="grid-3">
            {reviewData.reviews.map((r, i) => (
              <div key={r._id} style={{ background: i % 3 === 0 ? 'var(--cream)' : 'var(--white)', padding: '32px 28px', border: '1px solid var(--mist)', borderRadius: 2 }}>
                <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
                  {[1,2,3,4,5].map(s => <span key={s} style={{ color: 'var(--gold)', fontSize: '0.9rem' }}>★</span>)}
                </div>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', marginBottom: 12, color: 'var(--forest)' }}>"{r.title}"</h3>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '1rem', color: 'var(--text-body)', lineHeight: 1.8, marginBottom: 20 }}>
                  {r.content}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid var(--mist)', paddingTop: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--forest)', color: 'var(--gold-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Jost, sans-serif', fontWeight: 500, fontSize: '0.82rem', flexShrink: 0 }}>
                    {r.guestName.slice(0,2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '0.88rem', color: 'var(--forest)' }}>{r.guestName}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Review */}
      <section className="section" style={{ background: 'var(--cream)' }}>
        <div className="container-sm">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>Share Your Story</div>
            <h2 className="section-title">Write a Review</h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>Stayed with us? We'd love to hear about your experience at Villa Tesori.</p>
          </div>
          <div style={{ background: 'var(--white)', padding: '44px 48px', borderRadius: 2, border: '1px solid var(--mist)' }}>
            <form onSubmit={handleSubmit}>
              <div className="grid-2" style={{ gap: 20 }}>
                <div className="form-group">
                  <label className="form-label">Your Name *</label>
                  <input className="form-control" name="guestName" value={form.guestName} onChange={e => setForm(p=>({...p,guestName:e.target.value}))} required placeholder="Your name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input className="form-control" name="location" value={form.location} onChange={e => setForm(p=>({...p,location:e.target.value}))} placeholder="City, Country" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email (not published)</label>
                <input className="form-control" type="email" name="guestEmail" value={form.guestEmail} onChange={e => setForm(p=>({...p,guestEmail:e.target.value}))} placeholder="your@email.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Your Rating *</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {[1,2,3,4,5].map(s => (
                    <button type="button" key={s} onClick={() => setForm(p=>({...p,rating:s}))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.8rem', color: s <= form.rating ? 'var(--gold)' : 'var(--mist)', transition: 'color 0.2s' }}>★</button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Review Title *</label>
                <input className="form-control" name="title" value={form.title} onChange={e => setForm(p=>({...p,title:e.target.value}))} required placeholder="e.g. Best Wayanad Homestay!" />
              </div>
              <div className="form-group">
                <label className="form-label">Your Review *</label>
                <textarea className="form-control" name="content" value={form.content} onChange={e => setForm(p=>({...p,content:e.target.value}))} required placeholder="Share your experience..." style={{ minHeight: 140 }} />
              </div>
              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Review →'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
